import { useCallback, useEffect } from 'react';
import UIUtils from 'js/lib/UIUtils';
import { isUiModeCode } from 'new/utils/uiMode';
import {
  addDataSink,
  addDataSourceFromForm,
  setError,
  updateDataSourceFromForm,
  updateSinkInfo,
} from 'js/actions';
import { FORM_TYPE_CREATE_ELT } from 'js/constants/formTypes';
import { useForm } from 'new/elements/Form/useForm';
import { useDispatch, useSelector } from 'react-redux';
import useELT from 'new/components/flows/types/elt/CreateELTFlow/ELTFlow/useELT';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { useHistory } from 'react-router-dom';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import {
  ADD_DATA_SOURCE_REQUEST,
  ADD_SINK_REQUEST,
  GET_DATA_SOURCE_REQUEST,
  GET_SINK_REQUEST,
  PROBE_GET_TREE_REQUEST,
  UPDATE_SINK_INFO_REQUEST,
  UPDATE_SOURCE_INFO_REQUEST,
} from 'js/constants/actionTypes';
import useProbeTree from 'new/hooks/adapters/useProbeTree';
import { isEmpty } from 'lodash';
import useProbe from 'new/state/probe/repository/useProbe';
import usePaging from 'new/state/paging/repository/usePaging';
import createFlowService from 'new/components/flows/service/createFlowService';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import flowTypes from 'new/components/flows/types/flowTypes';
import useRequests from 'new/state/request/repository/useRequests';
import useFlowParams from 'new/components/flows/useFlowParams';
import { usePreviousPath } from 'new/hooks/usePreviousPath';
import { useHashedState } from 'new/state/hashedState/repository/useHashedState';

export default function useResourceCreation(editingId) {
  const {
    connectionType,
    selectedConnection,
    selectedCredential,
    resourceConfig,
    updateFormData,
    nexset,
    sinkConfig,
    sink,
    source,
    credentialId,
  } = useELT();
  const { isModal, onDismiss } = useFlowParams();

  const { setLoaderMessages } = useForm();
  const { showError } = useRequests();
  const { validate } = useFlowsSetup(FORM_TYPE_CREATE_ELT, flowTypes.elt);
  const flowService = createFlowService();

  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const history = useHistory();

  const { getMetaInfoById, getSourceConfigFields } = usePaging();
  const configFields = getSourceConfigFields(selectedConnection);

  const { databases } = useProbeTree(credentialId);
  const previousPath = usePreviousPath();
  const { save: saveHashedState } = useHashedState('flows.selected');

  const { getProbeData } = useProbe();
  const probe = getProbeData(credentialId);

  const metaInfo = getMetaInfoById(selectedConnection) || ({} as any);
  const { connectionCategory } = metaInfo;

  useEffect(() => {
    setLoaderMessages({
      [UPDATE_SOURCE_INFO_REQUEST]: 'Updating source info',
      [GET_SINK_REQUEST]: 'Loading destination',
      [GET_DATA_SOURCE_REQUEST]: 'Loading source',
      [PROBE_GET_TREE_REQUEST]: isEmpty(probe.data)
        ? `Connecting to ${metaInfo.displayName}`
        : null,
      [ADD_DATA_SOURCE_REQUEST]: 'Creating source',
      [UPDATE_SINK_INFO_REQUEST]: 'Updating destination info',
      [ADD_SINK_REQUEST]: 'Creating destination',
    });
  }, [probe.loading]);

  const navigateAfterRequest = (flowId) => {
    if (editingId) {
      history.goBack();
    } else {
      history.replace(`${ROUTE_CONNECT}/flows/elt/${flowId}/edit`, {
        flowId,
      });
    }
  };

  const createDefaultSinkConfig = () => {
    if (connectionCategory !== CONNECTION_CATEGORY_DATABASE) return {};
    return {
      mapping: {
        mode: 'auto',
        tracker_mode: 'NONE',
        table_name_prefix: '',
        table_name_suffix: '',
      },
      sink_type: selectedConnection,
      // 'flush.cron': '0 */2 * ? * * *',
      'insert.mode': 'UPSERT',
      'upsert.nulls': true,
    };
  };

  const createNewSink = () => {
    const configName = sinkConfig?.name || sinkConfig?.sourceName;
    if (configName) {
      delete sinkConfig.name;
      delete sinkConfig.sourceName;
    }

    // NEX-21470: in code mode the user owns sink_config. Skip the default
    // spread (mapping, sink_type, insert.mode, upsert.nulls, database) and
    // the UIUtils.reduceConfig pass that strips unknown keys.
    const userSinkConfig = resourceConfig.resourceConfig.sink_config;
    const codeMode = isUiModeCode(userSinkConfig) || isUiModeCode(sinkConfig);

    if (codeMode) {
      return {
        ...resourceConfig.resourceConfig,
        name:
          configName ||
          resourceConfig?.resourceConfig?.name ||
          sink?.name ||
          flowService.getSinkName({
            connectionCategory,
            selectedConnection,
            selectedCredential,
          }),
        data_set_id: nexset?.id,
        sink_config: { ...(userSinkConfig ?? sinkConfig ?? {}) },
      };
    }

    let newSink = {
      ...resourceConfig.resourceConfig,
      name:
        configName ||
        resourceConfig?.resourceConfig?.name ||
        sink?.name ||
        flowService.getSinkName({
          connectionCategory,
          selectedConnection,
          selectedCredential,
        }),
      data_set_id: nexset?.id,
      sink_config: {
        ...createDefaultSinkConfig(),
        // Loaded sink baseline must come before sinkConfig so the user's
        // form-state edits (e.g. data_format dropdown, iceberg.catalog change)
        // override the originally loaded values on save instead of being
        // overwritten by them.
        ...sink?.sink_config,
        ...sinkConfig,
        // Probe-derived default database is only meaningful when creating a
        // new sink. On edit the saved `database` already came in via
        // sink.sink_config above; injecting databases?.[0]?.name here would
        // either drop the saved value (when probe hasn't loaded → undefined)
        // or silently overwrite it with the first database in the probe tree.
        ...(connectionCategory === CONNECTION_CATEGORY_DATABASE && !sink
          ? { database: databases?.[0]?.name }
          : {}),
        ...userSinkConfig,
      },
    };
    return UIUtils.reduceConfig(newSink, configFields);
  };

  const canCreateSink = () => {
    if (connectionCategory !== CONNECTION_CATEGORY_DATABASE) {
      if (!validate(showError)) {
        return false;
      }
    }
    if (!resourceConfig) {
      dispatch(setError({ message: 'Your destination config is empty.' }));
      return false;
    }
    // Validation: Ensure nexset is available before creating sink
    // Since nexset is guaranteed to be loaded during initial setup, this should rarely fail
    if (!nexset?.id) {
      dispatch(setError({ message: 'Waiting for Nexset to be loaded...' }));
      return false;
    }
    return true;
  };

  const requestSinkCreation = async (newSink) => {
    let response;
    if (editingId) {
      response = await dispatch(updateSinkInfo(editingId, newSink));
    } else {
      response = await dispatch(addDataSink(selectedConnection, newSink, credentialId));
    }
    return response?.entities?.sinks?.[response?.result];
  };

  const redirectAfterCreation = (sink) => {
    if (sink?.id) {
      if (isModal) {
        onDismiss?.({ addedDataSinkId: sink.id, ...sink });
        return;
      }
      if (previousPath) {
        // Use setTimeout to ensure Redux state updates before navigation
        setTimeout(() => {
          history.goBack();
        }, 100);
      } else {
        // Save origin_node_id to hashedState for flow expansion
        if (sink.origin_node_id) {
          saveHashedState(sink.origin_node_id);
        }
        // Use setTimeout to ensure Redux state updates before navigation
        setTimeout(() => {
          history.replace(flowService.getPathToGoAfterCreation(previousPath));
        }, 100);
      }
    }
  };

  const createSink = useCallback(async () => {
    if (!canCreateSink()) return false;
    const newSink = createNewSink();
    try {
      const sink = await requestSinkCreation(newSink);
      redirectAfterCreation(sink);
      return !!sink?.id;
    } catch (e) {
      return false;
    }
  }, [
    source,
    nexset,
    sinkConfig,
    resourceConfig,
    configFields,
    connectionCategory,
    selectedConnection,
    credentialId,
  ]);

  return useCallback(async () => {
    return await createSink();
  }, [createSink]);
}
